import {AddBusinessOutlined} from "@mui/icons-material";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import {Card, IconButton, Stack, Typography} from "@mui/material";
import React from "react";
import {addPricing, addProduct, Business, Industry, Pricing, Product} from "../../../firebase/index.js";
import type {APXBaseOption, APXOptions} from "../../../reusables/APXMoreMenuWithPopover.jsx";
import APXMoreMenuWithPopover from "../../../reusables/APXMoreMenuWithPopover.jsx";
import type {SortOption, SortOptions} from "../../../reusables/APXSortOptions.jsx";
import APXSortOptions from "../../../reusables/APXSortOptions.jsx";
import {singleLineText} from "../../../reusables/js/APXCrucialStyles.js";
import {businessColour, productColour} from "../../../reusables/js/APXTheme.js";
import {nonPlusLimits, useInMemoryPlusStatus} from "../../../reusables/plus/APXPlus.js";
import {APXPlusRequiredAlert} from "../../../reusables/plus/APXPlusAlerts.jsx";
import AddNewPricingBSL from "./AddNewPricingBSL.jsx";
import PricingCard from "./PricingCard.jsx";


const sortOptions: SortOptions = {
	  BusinessNameAsc: {
			 label: "Business Name: A-Z",
			 compareFunction: (a: Pricing, b: Pricing) => a.businessName.localeCompare(b.businessName)
	  },
	  BusinessNameDesc: {
			 label: "Business Name: Z-A",
			 compareFunction: (a: Pricing, b: Pricing) => b.businessName.localeCompare(a.businessName)
	  },
	  PriceLowToHigh: {
			 label: "Price: Low to High",
			 compareFunction: (a: Pricing, b: Pricing) => a.price - b.price
	  },
	  PriceHighToLow: {
			 label: "Price: High to Low",
			 compareFunction: (a: Pricing, b: Pricing) => b.price - a.price
	  },
	  PricePerUnitLowToHigh: {
			 label: "Price Per Unit: Low to High",
			 compareFunction: (a: Pricing, b: Pricing) => {
					const u = a.unit.localeCompare(b.unit);
					if (u !== 0) return u;
					const q = a.quantity - b.quantity;
					if (q !== 0) return q;
					return a.price - b.price;
			 }
	  },
	  PricePerUnitHighToLow: {
			 label: "Price Per Unit: High to Low",
			 compareFunction: (a: Pricing, b: Pricing) => {
					const u = b.unit.localeCompare(a.unit);
					if (u !== 0) return u;
					const q = b.quantity - a.quantity;
					if (q !== 0) return q;
					return b.price - a.price;
			 }
	  }
};


export default function ProductCard({uid, i, p, ps, bs}: { uid: string, i: Industry, p: Product, ps: Pricing[], bs: Business[] }) {
	  
	  const industry: Industry = i;
	  const product: Product = p;
	  const pricings: Pricing[] = ps;
	  const businesses: Business[] = bs;
	  
	  const [expanded, setExpanded] = React.useState(false);
	  
	  const [sortedPricings, setSortedPricings] = React.useState(pricings);
	  React.useEffect(() => {
			 setSortedPricings(pricings);
	  }, [pricings]);
	  
	  const [openNewPricingBSL, setOpenNewPricingBSL] = React.useState(false);
	  
	  const menuOptions: APXOptions = {
			 delete: {
					label: "Delete",
					needsConfirmation: true,
					keepDisabled: false,
					onClick: React.useCallback(() => {
						  addProduct(uid, industry.industryKey, product.getKey(), null);
						  pricings && pricings.length > 0 && pricings.forEach((value) => addPricing(uid, industry.industryKey, value.getKey(), null));
					}, [uid, industry, product])
			 }
	  };
	  
	  const {isPlusUser} = useInMemoryPlusStatus();
	  
	  return (
				 <>
						<Card variant={"outlined"} sx={{p: 2, my: 2, borderColor: productColour}}>
							  <Stack spacing={2} alignItems={"stretch"} justifyContent={"center"}>
									 <Stack direction="row" spacing={2} justifyContent="space-between" alignItems="center">
											<Typography variant="h6" style={singleLineText} sx={{color: productColour}}>{product.productName} {"("}{product.productVariantName}{")"}</Typography>
											<Stack direction="row" spacing={0.5} alignItems="center">
												  <IconButton sx={{color: businessColour}} onClick={() => setOpenNewPricingBSL(true)}><AddBusinessOutlined/></IconButton>
												  <IconButton disabled={/*FALSY->*/ !!!pricings || pricings.length === 0} sx={{color: productColour}} onClick={() => setExpanded(/*NOT->*/ !expanded)}>
														 {expanded ? <ExpandLessIcon/> : <ExpandMoreIcon/>}</IconButton>
												  <APXMoreMenuWithPopover options={menuOptions} onConfirmedOptionSelected={(option: APXBaseOption) => option.onClick()}/>
											</Stack>
									 </Stack>
									 {expanded &&
												<APXSortOptions
														  sortOptions={sortOptions}
														  onSelect={(sortOption: SortOption) => {
																 setSortedPricings((prevState) => [...prevState].sort(sortOption.compareFunction));
														  }}
												/>
									 }
									 {expanded && sortedPricings &&
												sortedPricings.map((pricing) => <PricingCard key={pricing.getKey()} inBusiness={false} pp={pricing}/>)
									 }
									 {openNewPricingBSL ?
												(
														  (isPlusUser)
														  ||
														  (/*NOT->*/ !isPlusUser && /*TRUTHY->*/ !!pricings && pricings.length < nonPlusLimits.pri.numerical)
												) ?
														  <AddNewPricingBSL i={industry} p={product} ps={pricings} bs={businesses} uid={uid} onClose={() => setOpenNewPricingBSL(false)}/>
														  :
														  <APXPlusRequiredAlert/>
												:
												null
									 }
							  </Stack>
						</Card>
				 </>
	  );
}
